import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

export function LoginPage(): JSX.Element {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/upload';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Declarative redirect — no setState during render, no navigate() in body.
  // (Previously: `if (user) navigate(from)` caused React reentry loop.)
  if (user) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
        <h2 className="mb-4 text-2xl font-semibold text-ntr-primary">Вход</h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ntr-text">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-md border border-ntr-primary/30 px-3 py-2 text-sm shadow-sm focus:border-ntr-primary focus:outline-none focus:ring-1 focus:ring-ntr-primary"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ntr-text">
              Пароль
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-ntr-primary/30 px-3 py-2 text-sm shadow-sm focus:border-ntr-primary focus:outline-none focus:ring-1 focus:ring-ntr-primary"
            />
          </div>
          {err && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90 disabled:opacity-50"
          >
            {busy ? 'Вход…' : 'Войти'}
          </button>
        </form>
        <p className="mt-4 text-xs text-ntr-muted">
          Тестовый аккаунт: <code>f17.upload.test@gscg.group</code> /{' '}
          <code>F17UploadTest!2026</code>
        </p>
      </div>
    </div>
  );
}
